import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import {
	Star as StarIcon,
	StarBorder as StarBorderIcon,
	StarHalf as StarHalfIcon
} from "@mui/icons-material";
import { zoullooColors } from "../../../theme";

interface StarRatingProps {
	value: number;
	onChange?: (value: number) => void;
	readOnly?: boolean;
	size?: "small" | "medium" | "large";
	showLabel?: boolean;
}

const sizeMap = {
	small: "1.2rem",
	medium: "2rem",
	large: "2.5rem"
};

const StarRating: React.FC<StarRatingProps> = ({
	value,
	onChange,
	readOnly = false,
	size = "medium",
	showLabel = false
}) => {
	const [hoverValue, setHoverValue] = React.useState<number | null>(null);

	// In interactive mode (form input), use integer values only
	// In readOnly mode (display), support half-stars for averages
	const displayValue = hoverValue ?? value;

	const renderStar = (star: number) => {
		if (readOnly) {
			// ReadOnly mode: support half-stars
			if (star <= Math.floor(displayValue)) {
				// Full star
				return <StarIcon sx={{ fontSize: sizeMap[size] }} />;
			}
			if (star === Math.ceil(displayValue) && displayValue % 1 >= 0.25) {
				// Half star (show for values >= .25 of the star)
				return <StarHalfIcon sx={{ fontSize: sizeMap[size] }} />;
			}
			// Empty star
			return <StarBorderIcon sx={{ fontSize: sizeMap[size] }} />;
		}

		// Interactive mode: full or empty only
		return star <= displayValue ? (
			<StarIcon sx={{ fontSize: sizeMap[size] }} />
		) : (
			<StarBorderIcon sx={{ fontSize: sizeMap[size] }} />
		);
	};

	const getStarColor = (star: number) => {
		if (readOnly) {
			if (star <= Math.floor(displayValue)) return zoullooColors.warning.main;
			if (
				star === Math.ceil(displayValue) &&
				displayValue % 1 >= 0.25
			)
				return zoullooColors.warning.main;
			return zoullooColors.grey[300];
		}
		return star <= displayValue
			? zoullooColors.warning.main
			: zoullooColors.grey[300];
	};

	// Format label: show one decimal for averages, integer for whole numbers
	const formatLabel = (val: number) => {
		return Number.isInteger(val) ? `${val}/5` : `${val.toFixed(1)}/5`;
	};

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
			{[1, 2, 3, 4, 5].map(star => (
				<IconButton
					key={star}
					size="small"
					disableRipple={readOnly}
					onClick={() => !readOnly && onChange?.(star)}
					onMouseEnter={() => !readOnly && setHoverValue(star)}
					onMouseLeave={() => !readOnly && setHoverValue(null)}
					sx={{
						p: 0.25,
						color: getStarColor(star),
						"&:hover": {
							backgroundColor: "transparent"
						},
						cursor: readOnly ? "default" : "pointer",
						pointerEvents: readOnly ? "none" : "auto"
					}}
				>
					{renderStar(star)}
				</IconButton>
			))}
			{showLabel && value > 0 && (
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ ml: 0.5, fontWeight: 500 }}
				>
					{formatLabel(value)}
				</Typography>
			)}
		</Box>
	);
};

export default StarRating;
